import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import DisButton from '../components/design-system/DisButton';
import DisCard from '../components/design-system/DisCard';
import DisModal from '../components/design-system/DisModal';

const AdminNewsletterSection = () => {
  const { api } = useAuth();
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [question, setQuestion] = useState('');
  const [selectedNl, setSelectedNl] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [editDate, setEditDate] = useState('');
  const [stats, setStats] = useState(null);
  const [expandedNl, setExpandedNl] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => { loadNewsletters(); loadStats(); }, []);

  const loadNewsletters = async () => {
    try {
      const res = await api.get('/admin/newsletters');
      setNewsletters(res.data.newsletters || []);
    } catch (e) { console.error('Erreur chargement newsletters:', e); }
  };

  const loadStats = async () => {
    try {
      const res = await api.get('/admin/newsletter-stats');
      setStats(res.data);
    } catch (e) { console.error('Erreur chargement stats:', e); }
  };

  const handleGenerate = async () => {
    if (!question.trim()) return;
    setGenerating(true);
    try {
      const res = await api.post('/admin/newsletter/generate', { question: question.trim() });
      setQuestion('');
      await loadNewsletters();
      setSelectedNl(res.data);
      setShowPreview(true);
    } catch (e) {
      console.error('Erreur génération:', e);
      alert('Erreur lors de la génération de la newsletter');
    } finally { setGenerating(false); }
  };

  const handleSendTest = async (id) => {
    setSending(true);
    try {
      const res = await api.post(`/admin/newsletter/${id}/send-test`);
      alert(res.data.message || 'Test envoyé !');
    } catch (e) {
      console.error('Erreur envoi test:', e);
      alert('Erreur lors de l\'envoi du test');
    } finally { setSending(false); }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Annuler cette newsletter ?')) return;
    try {
      await api.delete(`/admin/newsletter/${id}`);
      await loadNewsletters();
    } catch (e) { console.error('Erreur annulation:', e); }
  };

  const handleDeletePermanently = async (id) => {
    if (!window.confirm('Supprimer définitivement cette campagne ? Cette action est irréversible.')) return;
    setDeleting(id);
    try {
      await api.delete(`/admin/newsletter/${id}/permanent`);
      if (expandedNl === id) setExpandedNl(null);
      await loadNewsletters();
      await loadStats();
    } catch (e) {
      console.error('Erreur suppression:', e);
      alert('Erreur lors de la suppression');
    } finally { setDeleting(null); }
  };

  const toLocalDatetimeString = (isoDate) => {
    if (!isoDate) return '';
    const d = new Date(isoDate);
    const offset = d.getTimezoneOffset();
    const local = new Date(d.getTime() - offset * 60000);
    return local.toISOString().slice(0, 16);
  };

  const handleUpdateDate = async (id) => {
    if (!editDate) return;
    try {
      const isoDate = new Date(editDate).toISOString();
      await api.post(`/admin/newsletter/${id}/schedule`, { scheduled_at: isoDate });
      setEditDate('');
      await loadNewsletters();
    } catch (e) { console.error('Erreur update date:', e); }
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'bg-gray-200 text-gray-700',
      scheduled: 'bg-blue-100 text-blue-700',
      sent: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700',
    };
    const labels = { draft: 'Brouillon', scheduled: 'Programmée', sent: 'Envoyée', cancelled: 'Annulée' };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const toggleExpand = (id) => {
    setExpandedNl(expandedNl === id ? null : id);
  };

  return (
    <DisCard className="p-6 mb-6" data-testid="newsletter-section">
      <h2 className="text-xl font-bold text-gray-900 mb-4">Newsletter — La Question de la Semaine</h2>

      {/* Statistiques globales */}
      {stats && (
        <div className="mb-6" data-testid="newsletter-stats">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="bg-blue-50 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-blue-700">{stats.subscribers}</p>
              <p className="text-xs text-blue-500">Abonnés</p>
            </div>
            <div className="bg-green-50 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-green-700">{stats.total_sent}</p>
              <p className="text-xs text-green-500">Emails envoyés</p>
            </div>
            <div className="bg-amber-50 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-amber-700">{stats.open_rate}%</p>
              <p className="text-xs text-amber-500">Taux d'ouverture</p>
            </div>
            <div className="bg-purple-50 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-purple-700">{stats.click_rate}%</p>
              <p className="text-xs text-purple-500">Taux de clic</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-gray-50 rounded-xl p-2 text-center">
              <p className="text-lg font-bold text-gray-700">{stats.sent_newsletters}</p>
              <p className="text-xs text-gray-400">Newsletters envoyées</p>
            </div>
            <div className="bg-red-50 rounded-xl p-2 text-center">
              <p className="text-lg font-bold text-red-600">{stats.total_unsubscribes}</p>
              <p className="text-xs text-red-400">Désinscriptions</p>
            </div>
            <div className="bg-orange-50 rounded-xl p-2 text-center">
              <p className="text-lg font-bold text-orange-600">{stats.total_bounces}</p>
              <p className="text-xs text-orange-400">Bounces</p>
            </div>
          </div>
        </div>
      )}

      {/* Formulaire de génération */}
      <div className="flex gap-2 mb-4" data-testid="newsletter-generate-form">
        <input
          type="text"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="Ex: Pourquoi le ciel est bleu ?"
          className="flex-1 px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:border-teal-500 text-sm"
          data-testid="newsletter-question-input"
          onKeyDown={(e) => e.key === 'Enter' && handleGenerate()}
        />
        <DisButton
          onClick={handleGenerate}
          disabled={generating || !question.trim()}
          className="bg-teal-500 hover:bg-teal-600 text-white whitespace-nowrap"
          data-testid="newsletter-generate-btn"
        >
          {generating ? 'Génération...' : 'Générer'}
        </DisButton>
      </div>

      {generating && (
        <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 mb-4 text-center" data-testid="newsletter-generating">
          <div className="animate-pulse text-teal-700 text-sm">
            Génération des 3 réponses par tranche d'âge + quiz en cours... (30-60s)
          </div>
        </div>
      )}

      {/* Liste des newsletters */}
      <div className="space-y-3" data-testid="newsletter-list">
        {newsletters.length === 0 && !generating && (
          <p className="text-gray-400 text-sm text-center py-4">Aucune newsletter créée</p>
        )}
        {newsletters.map(nl => (
          <div key={nl.id} className="bg-gray-50 rounded-xl p-4" data-testid={`newsletter-item-${nl.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div
                className="flex-1 min-w-0 cursor-pointer"
                onClick={() => toggleExpand(nl.id)}
                data-testid={`newsletter-toggle-${nl.id}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  {getStatusBadge(nl.status)}
                  <span className="text-xs text-gray-400">{formatDate(nl.created_at)}</span>
                  <span className="text-xs text-gray-300">{expandedNl === nl.id ? '▲' : '▼'}</span>
                </div>
                <p className="text-sm font-medium text-gray-800 truncate">« {nl.question} »</p>
                {nl.scheduled_at && nl.status !== 'cancelled' && (
                  <p className="text-xs text-gray-500 mt-1">
                    Programmée : {formatDate(nl.scheduled_at)}
                  </p>
                )}
                {nl.sent_at && (
                  <p className="text-xs text-green-600 mt-1">Envoyée le {formatDate(nl.sent_at)}</p>
                )}
                {nl.status === 'sent' && nl.sent_count > 0 && (
                  <div className="flex gap-3 mt-1">
                    <span className="text-xs text-gray-500">{nl.sent_count} envois</span>
                    {(nl.open_count || 0) > 0 && <span className="text-xs text-amber-600">{nl.open_count} ouvertures</span>}
                    {(nl.click_count || 0) > 0 && <span className="text-xs text-purple-600">{nl.click_count} clics</span>}
                  </div>
                )}
              </div>
              <div className="flex gap-1 shrink-0">
                {(nl.status === 'draft' || nl.status === 'scheduled') && (
                  <>
                    <button
                      onClick={() => handleSendTest(nl.id)}
                      disabled={sending}
                      className="px-2 py-1 text-xs bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                      data-testid={`newsletter-test-${nl.id}`}
                    >
                      {sending ? '...' : 'Tester'}
                    </button>
                    <button
                      onClick={() => handleCancel(nl.id)}
                      className="px-2 py-1 text-xs bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                      data-testid={`newsletter-cancel-${nl.id}`}
                    >
                      Annuler
                    </button>
                  </>
                )}
                {nl.status === 'cancelled' && (
                  <button
                    onClick={() => handleDeletePermanently(nl.id)}
                    disabled={deleting === nl.id}
                    className="px-2 py-1 text-xs bg-red-500 text-white rounded-lg hover:bg-red-600"
                    data-testid={`newsletter-delete-${nl.id}`}
                  >
                    {deleting === nl.id ? '...' : 'Supprimer'}
                  </button>
                )}
              </div>
            </div>

            {/* Détails étendus au clic */}
            {expandedNl === nl.id && (
              <div className="mt-3 pt-3 border-t border-gray-200" data-testid={`newsletter-detail-${nl.id}`}>
                {/* Stats détaillées pour campagne envoyée */}
                {nl.status === 'sent' && (
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3">
                    <div className="bg-white rounded-lg p-2 text-center border">
                      <p className="text-lg font-bold text-gray-800">{nl.sent_count || 0}</p>
                      <p className="text-[10px] text-gray-400">Envoyés</p>
                    </div>
                    <div className="bg-white rounded-lg p-2 text-center border">
                      <p className="text-lg font-bold text-amber-600">{nl.open_count || 0}</p>
                      <p className="text-[10px] text-gray-400">Ouvertures</p>
                    </div>
                    <div className="bg-white rounded-lg p-2 text-center border">
                      <p className="text-lg font-bold text-purple-600">{nl.click_count || 0}</p>
                      <p className="text-[10px] text-gray-400">Clics</p>
                    </div>
                    <div className="bg-white rounded-lg p-2 text-center border">
                      <p className="text-lg font-bold text-amber-700">
                        {nl.sent_count > 0 ? Math.round((nl.open_count || 0) / nl.sent_count * 100) : 0}%
                      </p>
                      <p className="text-[10px] text-gray-400">Taux ouv.</p>
                    </div>
                    <div className="bg-white rounded-lg p-2 text-center border">
                      <p className="text-lg font-bold text-purple-700">
                        {nl.sent_count > 0 ? Math.round((nl.click_count || 0) / nl.sent_count * 100) : 0}%
                      </p>
                      <p className="text-[10px] text-gray-400">Taux clic</p>
                    </div>
                    <div className="bg-white rounded-lg p-2 text-center border">
                      <p className="text-lg font-bold text-red-500">{(nl.unsubscribe_count || 0) + (nl.bounce_count || 0)}</p>
                      <p className="text-[10px] text-gray-400">Désins./Bounce</p>
                    </div>
                  </div>
                )}

                {/* Erreurs pour campagne envoyée */}
                {nl.status === 'sent' && (nl.error_count || 0) > 0 && (
                  <p className="text-xs text-red-500 mb-2">{nl.error_count} erreur(s) d'envoi</p>
                )}

                {/* Info pour campagnes non envoyées */}
                {nl.status !== 'sent' && nl.status !== 'cancelled' && (
                  <p className="text-xs text-gray-400 italic mb-2">Les stats de campagne seront disponibles après l'envoi</p>
                )}

                {/* Prévisualisation du contenu */}
                <button
                  onClick={(e) => { e.stopPropagation(); setSelectedNl(nl); setShowPreview(true); }}
                  className="text-xs text-teal-600 hover:text-teal-800 underline"
                  data-testid={`newsletter-preview-${nl.id}`}
                >
                  Voir le contenu
                </button>
              </div>
            )}

            {/* Programmation inline */}
            {(nl.status === 'draft' || nl.status === 'scheduled') && (
              <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-200">
                <input
                  type="datetime-local"
                  defaultValue={toLocalDatetimeString(nl.scheduled_at)}
                  onChange={(e) => setEditDate(e.target.value)}
                  className="text-xs border border-gray-300 rounded-lg px-2 py-1 flex-1"
                  data-testid={`newsletter-date-${nl.id}`}
                />
                <button
                  onClick={() => handleUpdateDate(nl.id)}
                  className="text-xs px-2 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                  data-testid={`newsletter-schedule-${nl.id}`}
                >
                  Programmer
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Modal prévisualisation */}
      {showPreview && selectedNl && (
        <DisModal isOpen={showPreview} onClose={() => setShowPreview(false)} title="Prévisualisation Newsletter">
          <div className="space-y-4 max-h-96 overflow-y-auto">
            <p className="font-bold text-lg text-gray-800">« {selectedNl.question} »</p>
            {selectedNl.responses && Object.entries(selectedNl.responses).map(([bracket, data]) => (
              <div key={bracket} className="bg-gray-50 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                    bracket === 'small' ? 'bg-pink-100 text-pink-700' : 'bg-purple-100 text-purple-700'
                  }`}>
                    {bracket === 'small' ? '0-4 ans' : '5+ ans'}
                  </span>
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{data.text}</p>
              </div>
            ))}
            {selectedNl.quiz_questions && Object.entries(selectedNl.quiz_questions).map(([bracket, questions]) => (
              <div key={`quiz-${bracket}`} className="border-t pt-2">
                <p className="text-xs font-bold text-gray-500 mb-1">Quiz {bracket === 'small' ? '0-4' : '5+'} ans</p>
                {questions.map((q, i) => (
                  <p key={i} className="text-xs text-gray-600">Q{i+1}: {q.question}</p>
                ))}
              </div>
            ))}
          </div>
          <div className="mt-4 flex gap-2 justify-end">
            <DisButton onClick={() => setShowPreview(false)} className="bg-gray-200 text-gray-700">Fermer</DisButton>
          </div>
        </DisModal>
      )}
    </DisCard>
  );
};

export default AdminNewsletterSection;
